// src/pages/TransactionStatistics.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { useAuth } from "../context/AuthContext";
import {
    FaSyncAlt,
    FaArrowDown,
    FaArrowUp,
    FaChartLine,
    FaCalendarAlt,
    FaDownload,
    FaFilter,
    FaMoneyBillWave,
    FaExchangeAlt,
} from "react-icons/fa";
import { IoIosTrendingDown, IoIosTrendingUp } from "react-icons/io";

/* ---------------- helpers ---------------- */
const pad2 = (n) => String(n).padStart(2, "0");
const getTodayLocalISODate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const toUTCishDate = (dstr) => {
    if (!dstr) return null;
    const [y, m, d] = String(dstr).split("-").map((x) => Number(x));
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d);
    return Number.isFinite(dt.getTime()) ? dt : null;
};

const diffDays = (fromStr, toStr) => {
    const a = toUTCishDate(fromStr);
    const b = toUTCishDate(toStr);
    if (!a || !b) return null;
    const ms = b.getTime() - a.getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
};

const nf2 = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    style: "currency",
    currency: "USD",
});

const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const formatNum = (v) => nf2.format(safeNum(v));

const formatCount = (v) => {
    const num = safeNum(v);
    return new Intl.NumberFormat("en-IN").format(num);
};

const DEFAULT_DATA = {
    deposit: {
        total: { count: 0, value: 0 },
        success: { count: 0, value: 0 },
        pending: { count: 0, value: 0 },
        rejected: { count: 0, value: 0 },
    },
    withdrawl: {
        total: { count: 0, value: 0 },
        success: { count: 0, value: 0 },
        pending: { count: 0, value: 0 },
        rejected: { count: 0, value: 0 },
    },
    profit: 0,
};

const StatusIndicator = ({ status, count, value }) => {
    const config = {
        success: {
            color: "bg-success",
            text: "Success",
            icon: "✓",
            bg: "bg-success-50",
            border: "border-success-100",
        },
        pending: {
            color: "bg-warning",
            text: "Pending",
            icon: "⟳",
            bg: "bg-warning-50",
            border: "border-warning-100",
        },
        rejected: {
            color: "bg-danger",
            text: "Rejected",
            icon: "✗",
            bg: "bg-danger-50",
            border: "border-danger-100",
        },
        total: {
            color: "bg-primary",
            text: "Total",
            icon: "∑",
            bg: "bg-primary-50",
            border: "border-primary-100",
        },
    };

    const { color, text, icon, bg, border } = config[status] || config.total;

    return (
        <div className={`p-4 rounded-3 ${bg} ${border} border h-100`}>
            <div className="d-flex align-items-center gap-3 mb-3">
                <div
                    className={`${color} text-white rounded-circle d-flex align-items-center justify-content-center`}
                    style={{ width: 40, height: 40 }}
                >
                    <span className="fw-bold">{icon}</span>
                </div>
                <div>
                    <div className="fw-semibold text-dark">{text}</div>
                    <div className="small text-muted">Transactions</div>
                </div>
            </div>

            <div className="row g-2">
                <div className="col-6">
                    <div className="text-muted small">Count</div>
                    <div className="fw-bold fs-5">{formatCount(count)}</div>
                </div>
                <div className="col-6">
                    <div className="text-muted small">Value</div>
                    <div className="fw-bold fs-5">{formatNum(value)}</div>
                </div>
            </div>
        </div>
    );
};

const ProgressBar = ({ value, total, color }) => {
    const percentage = total > 0 ? (safeNum(value) / safeNum(total)) * 100 : 0;

    return (
        <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1">
                <div className="progress" style={{ height: 8 }}>
                    <div
                        className={`progress-bar ${color}`}
                        role="progressbar"
                        style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
                        aria-valuenow={percentage}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </div>
            </div>
            <div className="small text-muted" style={{ minWidth: 45 }}>
                {percentage.toFixed(1)}%
            </div>
        </div>
    );
};

export default function TransactionStatistics() {
    const { logout } = useAuth();

    const today = useMemo(() => getTodayLocalISODate(), []);
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(DEFAULT_DATA);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    const validateDatesOrToast = useCallback((fd, td) => {
        if (!fd || !td) {
            toast.error("From Date and To Date are required");
            return false;
        }

        const fromDt = toUTCishDate(fd);
        const toDt = toUTCishDate(td);
        const todayDt = toUTCishDate(getTodayLocalISODate());

        if (!fromDt || !toDt) {
            toast.error("Invalid date format");
            return false;
        }

        if (toDt.getTime() < fromDt.getTime()) {
            toast.error("To Date must be equal to or after From Date");
            return false;
        }

        if (todayDt) {
            if (fromDt.getTime() > todayDt.getTime() || toDt.getTime() > todayDt.getTime()) {
                toast.error("Future dates are not allowed");
                return false;
            }
        }

        const days = diffDays(fd, td);
        if (days === null) {
            toast.error("Invalid date range");
            return false;
        }
        if (days > 90) {
            toast.error("Date range is too large (max 90 days)");
            return false;
        }

        return true;
    }, []);

    const fetchStats = useCallback(
        async (opts = {}) => {
            const fd = opts?.from_date ?? fromDate;
            const td = opts?.to_date ?? toDate;

            if (!validateDatesOrToast(fd, td)) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const response = await axios({
                    method: "post",
                    // ✅ FIXED: broken quote
                    url: `${BASE_ENDPOINT}/admin/statistics/transaction`,
                    maxBodyLength: Infinity,
                    headers: { "Content-Type": "application/json" },
                    data: {
                        token: localStorage.getItem("token"),
                        from_date: fd,
                        to_date: td,
                    },
                    timeout: 15000,
                });

                const out = response?.data;

                if (out?.error) {
                    const errText = String(out?.error || "").toLowerCase();
                    if (errText.includes("session")) {
                        logout();
                        return;
                    }
                    toast.error(out?.msg || out?.error || "Failed to load transaction statistics");
                    setData(DEFAULT_DATA);
                } else {
                    setData(out?.data ? { ...DEFAULT_DATA, ...out.data } : DEFAULT_DATA);
                    setLastUpdated(new Date());
                }
            } catch (err) {
                console.log(err);
                toast.error("Failed to load transaction statistics");
                setData(DEFAULT_DATA);
            } finally {
                setLoading(false);
            }
        },
        [fromDate, toDate, validateDatesOrToast, logout]
    );

    useEffect(() => {
        fetchStats({ from_date: today, to_date: today });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [today]);

    const onApply = async (e) => {
        e.preventDefault();
        await fetchStats({ from_date: fromDate, to_date: toDate });
    };

    const onReset = async () => {
        const t = getTodayLocalISODate();
        setFromDate(t);
        setToDate(t);
        await fetchStats({ from_date: t, to_date: t });
    };

    const deposit = data?.deposit || DEFAULT_DATA.deposit;
    const withdrawl = data?.withdrawl || DEFAULT_DATA.withdrawl;
    const profit = safeNum(data?.profit);

    // Calculate success rates
    const depositSuccessRate = deposit.total.count > 0 ? (deposit.success.count / deposit.total.count) * 100 : 0;
    const withdrawlSuccessRate = withdrawl.total.count > 0 ? (withdrawl.success.count / withdrawl.total.count) * 100 : 0;

    // Calculate totals
    const totalDeposits = safeNum(deposit.total.value);
    const totalWithdrawals = safeNum(withdrawl.total.value);
    const netFlow = totalDeposits - totalWithdrawals;

    const exportData = () => {
        const rows = [
            ["Transaction Statistics Report"],
            [`From: ${fromDate}`, `To: ${toDate}`],
            [],
            ["DEPOSIT"],
            ["Status", "Count", "Value"],
            ["Total", deposit.total.count, deposit.total.value],
            ["Success", deposit.success.count, deposit.success.value],
            ["Pending", deposit.pending.count, deposit.pending.value],
            ["Rejected", deposit.rejected.count, deposit.rejected.value],
            [],
            ["WITHDRAWAL"],
            ["Status", "Count", "Value"],
            ["Total", withdrawl.total.count, withdrawl.total.value],
            ["Success", withdrawl.success.count, withdrawl.success.value],
            ["Pending", withdrawl.pending.count, withdrawl.pending.value],
            ["Rejected", withdrawl.rejected.count, withdrawl.rejected.value],
            [],
            ["SUMMARY"],
            ["Profit", profit],
            ["Net Cash Flow", netFlow],
            ["Deposit Success Rate", `${depositSuccessRate.toFixed(2)}%`],
            ["Withdrawal Success Rate", `${withdrawlSuccessRate.toFixed(2)}%`],
        ];

        const csvContent = rows
            .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `transaction_stats_${fromDate}_to_${toDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <PageTransition>
            {/* Header Section */}
            <div className="row g-3 mb-4">
                <div className="col-12">
                    <div className="card border-0 bg-gradient-primary text-white shadow-lg">
                        <div className="card-body">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                <div>
                                    <h1 className="fw-bold mb-2">Transaction Statistics</h1>
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <FaCalendarAlt />
                                            <span>
                                                {fromDate} to {toDate}
                                            </span>
                                        </div>
                                        {lastUpdated && (
                                            <div className="text-white-75">
                                                Updated:{" "}
                                                {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm d-flex align-items-center gap-2"
                                        onClick={exportData}
                                        disabled={loading}
                                    >
                                        <FaDownload />
                                        Export
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm d-flex align-items-center gap-2"
                                        onClick={() => setShowFilters(!showFilters)}
                                    >
                                        <FaFilter />
                                        Filters
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm d-flex align-items-center gap-2"
                                        onClick={() => fetchStats({ from_date: fromDate, to_date: toDate })}
                                        disabled={loading}
                                    >
                                        <FaSyncAlt className={loading ? "spin" : ""} />
                                        Refresh
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            {showFilters && (
                <div className="row g-3 mb-4">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="fw-semibold mb-0">Date Filters</h5>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={onReset} disabled={loading}>
                                        Reset to Today
                                    </button>
                                </div>

                                <form className="row g-3" onSubmit={onApply}>
                                    <div className="col-12 col-md-5">
                                        <label className="form-label text-muted mb-2">From Date</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">
                                                <FaCalendarAlt className="text-muted" />
                                            </span>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fromDate}
                                                max={today}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setFromDate(v);
                                                    if (toDate && v && new Date(toDate) < new Date(v)) {
                                                        setToDate(v);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-5">
                                        <label className="form-label text-muted mb-2">To Date</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">
                                                <FaCalendarAlt className="text-muted" />
                                            </span>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={toDate}
                                                min={fromDate || undefined}
                                                max={today}
                                                onChange={(e) => setToDate(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-2 d-flex align-items-end">
                                        <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                                            Apply
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading ? (
                <div className="row g-3">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body text-center py-5">
                                <div className="spinner-border text-primary mb-3" style={{ width: "3rem", height: "3rem" }} role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <h5 className="text-muted">Loading transaction statistics...</h5>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="row g-3 mb-4">
                        <div className="col-12 col-lg-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-3">
                                                <FaMoneyBillWave size={20} />
                                            </div>
                                            <h5 className="fw-semibold mb-0">Total Deposits</h5>
                                        </div>
                                        <FaArrowDown className="text-success" />
                                    </div>
                                    <h2 className="fw-bold mb-1">{formatNum(totalDeposits)}</h2>
                                    <div className="text-muted small">{formatCount(deposit.total.count)} transactions</div>
                                    <div className="mt-3">
                                        <ProgressBar value={depositSuccessRate} total={100} color="bg-success" />
                                        <div className="small text-muted mt-1">Success rate: {depositSuccessRate.toFixed(1)}%</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-info bg-opacity-10 text-info p-2 rounded-3">
                                                <FaExchangeAlt size={20} />
                                            </div>
                                            <h5 className="fw-semibold mb-0">Total Withdrawals</h5>
                                        </div>
                                        <FaArrowUp className="text-danger" />
                                    </div>
                                    <h2 className="fw-bold mb-1">{formatNum(totalWithdrawals)}</h2>
                                    <div className="text-muted small">{formatCount(withdrawl.total.count)} transactions</div>
                                    <div className="mt-3">
                                        <ProgressBar value={withdrawlSuccessRate} total={100} color="bg-success" />
                                        <div className="small text-muted mt-1">Success rate: {withdrawlSuccessRate.toFixed(1)}%</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className={`bg-opacity-10 p-2 rounded-3 ${profit >= 0 ? "bg-success" : "bg-danger"}`}>
                                                <FaChartLine size={20} className={profit >= 0 ? "text-success" : "text-danger"} />
                                            </div>
                                            <h5 className="fw-semibold mb-0">Net Profit</h5>
                                        </div>
                                        {profit >= 0 ? <IoIosTrendingUp className="text-success" /> : <IoIosTrendingDown className="text-danger" />}
                                    </div>
                                    <h2 className={`fw-bold mb-1 ${profit >= 0 ? "text-success" : "text-danger"}`}>{formatNum(profit)}</h2>
                                    <div className="text-muted small">After all transactions</div>
                                    <div className="mt-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="text-muted small">Cash Flow</div>
                                            <div className={`fw-semibold ${netFlow >= 0 ? "text-success" : "text-danger"}`}>{formatNum(netFlow)}</div>
                                        </div>
                                        <ProgressBar
                                            value={Math.abs(netFlow)}
                                            total={Math.max(totalDeposits, totalWithdrawals)}
                                            color={netFlow >= 0 ? "bg-success" : "bg-danger"}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Statistics */}
                    <div className="row g-3">
                        {/* Deposit Statistics */}
                        <div className="col-12 col-lg-6">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white border-0 pt-3 pb-2">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}>
                                                <FaArrowDown size={14} />
                                            </div>
                                            Deposit Statistics
                                        </h5>
                                        <span className="badge bg-primary">{formatCount(deposit.total.count)} transactions</span>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <StatusIndicator status="total" count={deposit.total.count} value={deposit.total.value} />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <StatusIndicator status="success" count={deposit.success.count} value={deposit.success.value} />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <StatusIndicator status="pending" count={deposit.pending.count} value={deposit.pending.value} />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <StatusIndicator status="rejected" count={deposit.rejected.count} value={deposit.rejected.value} />
                                        </div>
                                    </div>

                                    <div className="mt-4 pt-3 border-top">
                                        <h6 className="fw-semibold mb-3">Status Distribution</h6>
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className="small text-muted">Success</span>
                                                    <span className="small fw-semibold">{formatNum(deposit.success.value)}</span>
                                                </div>
                                                <ProgressBar value={deposit.success.value} total={deposit.total.value} color="bg-success" />
                                            </div>

                                            <div className="col-12">
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className="small text-muted">Pending</span>
                                                    <span className="small fw-semibold">{formatNum(deposit.pending.value)}</span>
                                                </div>
                                                <ProgressBar value={deposit.pending.value} total={deposit.total.value} color="bg-warning" />
                                            </div>

                                            <div className="col-12">
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className="small text-muted">Rejected</span>
                                                    <span className="small fw-semibold">{formatNum(deposit.rejected.value)}</span>
                                                </div>
                                                <ProgressBar value={deposit.rejected.value} total={deposit.total.value} color="bg-danger" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Withdrawal Statistics */}
                        <div className="col-12 col-lg-6">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white border-0 pt-3 pb-2">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                                            <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}>
                                                <FaArrowUp size={14} />
                                            </div>
                                            Withdrawal Statistics
                                        </h5>
                                        <span className="badge bg-info">{formatCount(withdrawl.total.count)} transactions</span>
                                    </div>
                                </div>

                                <div className="card-body">
                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <StatusIndicator status="total" count={withdrawl.total.count} value={withdrawl.total.value} />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <StatusIndicator status="success" count={withdrawl.success.count} value={withdrawl.success.value} />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <StatusIndicator status="pending" count={withdrawl.pending.count} value={withdrawl.pending.value} />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <StatusIndicator status="rejected" count={withdrawl.rejected.count} value={withdrawl.rejected.value} />
                                        </div>
                                    </div>

                                    <div className="mt-4 pt-3 border-top">
                                        <h6 className="fw-semibold mb-3">Status Distribution</h6>
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className="small text-muted">Success</span>
                                                    <span className="small fw-semibold">{formatNum(withdrawl.success.value)}</span>
                                                </div>
                                                <ProgressBar value={withdrawl.success.value} total={withdrawl.total.value} color="bg-success" />
                                            </div>

                                            <div className="col-12">
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className="small text-muted">Pending</span>
                                                    <span className="small fw-semibold">{formatNum(withdrawl.pending.value)}</span>
                                                </div>
                                                <ProgressBar value={withdrawl.pending.value} total={withdrawl.total.value} color="bg-warning" />
                                            </div>

                                            <div className="col-12">
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className="small text-muted">Rejected</span>
                                                    <span className="small fw-semibold">{formatNum(withdrawl.rejected.value)}</span>
                                                </div>
                                                <ProgressBar value={withdrawl.rejected.value} total={withdrawl.total.value} color="bg-danger" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <style>{`
                .spin { animation: spin 0.9s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }

                .bg-gradient-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }

                .bg-primary-50 { background-color: rgba(102, 126, 234, 0.1); }
                .border-primary-100 { border-color: rgba(102, 126, 234, 0.2) !important; }

                .bg-success-50 { background-color: rgba(40, 167, 69, 0.1); }
                .border-success-100 { border-color: rgba(40, 167, 69, 0.2) !important; }

                .bg-warning-50 { background-color: rgba(255, 193, 7, 0.1); }
                .border-warning-100 { border-color: rgba(255, 193, 7, 0.2) !important; }

                .bg-danger-50 { background-color: rgba(220, 53, 69, 0.1); }
                .border-danger-100 { border-color: rgba(220, 53, 69, 0.2) !important; }

                .card {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }

                .card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08) !important;
                }

                .progress {
                    border-radius: 10px;
                    background-color: #f8f9fa;
                }

                .progress-bar {
                    border-radius: 10px;
                }
            `}</style>
        </PageTransition>
    );
}
